import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Plot from 'react-plotly.js';
import { useNavigate } from 'react-router-dom';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const UserPage = ({ user, onLogout }) => {
  const navigate = useNavigate();

  const OUTPUTS_FOLDER = "./outputs";

  const [outputFiles, setOutputFiles] = useState([]);
  const [plotJsonData, setPlotJsonData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchOutputFiles();
  }, []);

  const fetchOutputFiles = async () => {
    try {
      const listRes = await axios.post(`${backendUrl}/api/list_outputs`);
      if (listRes.data.success) {
        const files = listRes.data.files;
        setOutputFiles(files);

        const jsonFile = files.find(f => f.endsWith('.json'));
        if (jsonFile) {
          fetchPlotJson(jsonFile);
        } else {
          setPlotJsonData(null);
        }
      } else {
        setError('Failed to fetch output files or 3D layout');
      }
    } catch (err) {
      setError('Error fetching output files');
      console.error(err);
    }
  };

  const fetchPlotJson = async (filename) => {
    try {
      const res = await axios.get(`${backendUrl}/api/get_output_file`, {
        params: { filename },
        responseType: 'text',
      });

      const data = JSON.parse(res.data);
      setPlotJsonData(data);
    } catch (err) {
      setError('Could not fetch or parse 3D layout JSON');
      console.error(err);
    }
  };

  const handleLogout = () => {
    navigate('/login');
  };


  return (
    <div className="min-h-screen bg-[#212121] text-white font-sans">
      <div className="w-full max-w-5xl mx-auto px-6 py-10 relative">
        <button
          onClick={handleLogout}
          className="absolute top-6 right-6 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded shadow-md transition"
        >
          Logout
        </button>

        <h1 className="text-3xl font-bold mb-6">
          Welcome, {user?.username || 'User'}!
        </h1>

        {/* Image and Video Cards Side by Side */}
        {outputFiles.length > 0 && (
          <div className="w-full max-w-[1000px] mx-auto mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* PNG Image Cards */}
            {outputFiles.filter(f => f.endsWith('.png')).map(img => (
              <div
                key={img}
                className="bg-[#2E2E2E] rounded-lg text-white shadow flex flex-col items-center justify-center aspect-square p-4"
                style={{ aspectRatio: '1 / 1' }}
              >
                <h3 className="text-orange-400 font-semibold mb-2">Image Output</h3>
                <img
                  src={`${backendUrl}/api/get_output_file?filename=${encodeURIComponent(img)}`}
                  alt="Output PNG"
                  className="rounded w-auto h-full mt-2 max-h-[350px] object-contain"
                />
              </div>
            ))}

            {/* MP4 Video Cards */}
            {outputFiles.filter(f => f.endsWith('.mp4')).map(video => (
              <div
                key={video}
                className="bg-[#2E2E2E] rounded-lg text-white shadow flex flex-col items-center justify-center aspect-square p-4"
                style={{ aspectRatio: '1 / 1' }}
              >
                <h3 className="text-orange-400 font-semibold mb-2">Video Output</h3>
                <video controls className="w-full mt-2 h-auto max-h-[350px] rounded">
                  <source
                    src={`${backendUrl}/api/get_output_file?filename=${encodeURIComponent(video)}`}
                    type="video/mp4"
                  />
                  Your browser does not support the video tag.
                </video>
              </div>
            ))}
          </div>
        )}

        {/* 3D Interactive Layout */}
        {plotJsonData && (
          <div className="bg-[#2E2E2E] mt-6 p-4 rounded-lg text-white shadow">
            <h2 className="text-orange-400 font-semibold mb-2">3D Packing Layout</h2>
            <Plot
              data={plotJsonData.data}
              layout={plotJsonData.layout}
              config={{ responsive: true }}
              style={{ width: '100%', height: '600px' }}
            />
          </div>
        )}

        {/* Error */}
        {error && (
          <p className="mt-4 text-center text-red-400">{error}</p>
        )}

        {!outputFiles.length && !error && (
          <p className="mt-4 text-center text-gray-400">No outputs available yet.</p>
        )}
      </div>
    </div>
  );
};

export default UserPage;
